import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Radar, 
  TrendingUp,
  Zap,
  Target,
  Loader2,
  Sparkles,
  X,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  business: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  career: 'bg-green-500/20 text-green-400 border-green-500/30',
  investment: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  trend: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
};

const urgencyColors = {
  high: 'text-red-400',
  medium: 'text-yellow-400',
  low: 'text-green-400'
};

export default function OpportunityRadarPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [filter, setFilter] = useState('all');
  
  // Scan form
  const [scanForm, setScanForm] = useState({
    interests: '',
    industry: ''
  });

  const loadOpportunities = useCallback(async () => {
    try {
      const params = filter !== 'all' ? `?category=${filter}` : '';
      const response = await axios.get(`${API_URL}/api/growth/opportunities${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setOpportunities(response.data.opportunities || []);
    } catch (error) {
      console.error('Failed to load opportunities');
    }
  }, [filter, token]);

  useEffect(() => {
    loadOpportunities();
  }, [loadOpportunities]);

  const scanForOpportunities = async () => {
    setScanning(true);
    try {
      const response = await axios.post(`${API_URL}/api/growth/opportunities/scan`, {
        interests: scanForm.interests.split(',').map(i => i.trim()).filter(Boolean),
        industry: scanForm.industry
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setOpportunities(prev => [...response.data.opportunities, ...prev]);
      toast.success(`Found ${response.data.opportunities.length} new opportunities!`);
    } catch (error) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const dismissOpportunity = async (oppId) => {
    try {
      await axios.delete(`${API_URL}/api/growth/opportunities/${oppId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setOpportunities(prev => prev.filter(o => o.opportunity_id !== oppId));
      toast.success('Opportunity dismissed');
    } catch (error) {
      toast.error('Failed to dismiss');
    }
  };

  const categories = ['all', 'business', 'career', 'investment', 'trend'];

  return (
    <div className="max-w-6xl mx-auto" data-testid="opportunity-radar-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">
          <span className="glow-text">AI Opportunity Radar</span>
        </h1>
        <p className="text-gray-400">Discover personalized opportunities powered by AI</p>
      </div>

      {/* Scan Section */}
      <GlassCard className="p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm text-gray-400 mb-2">Your Interests</label>
            <input
              type="text"
              value={scanForm.interests}
              onChange={(e) => setScanForm(prev => ({ ...prev, interests: e.target.value }))}
              className="input-field w-full"
              placeholder="e.g., AI, startups, investing (comma separated)"
              data-testid="interests-input"
            />
          </div>
          <div className="w-full md:w-48">
            <label className="block text-sm text-gray-400 mb-2">Industry</label>
            <input
              type="text"
              value={scanForm.industry}
              onChange={(e) => setScanForm(prev => ({ ...prev, industry: e.target.value }))}
              className="input-field w-full"
              placeholder="e.g., Tech"
              data-testid="industry-input"
            />
          </div>
          <button
            onClick={scanForOpportunities}
            disabled={scanning}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
            data-testid="scan-btn"
          >
            {scanning ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Radar className="w-5 h-5" />
                Scan for Opportunities
              </>
            )}
          </button>
        </div>
      </GlassCard>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`px-4 py-2 rounded-full whitespace-nowrap capitalize transition-all ${
              filter === cat
                ? 'bg-primary text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Opportunities Grid */}
      {opportunities.length === 0 ? (
        <GlassCard className="p-12 text-center">
          <Radar className="w-16 h-16 text-primary/30 mx-auto mb-4" />
          <h3 className="font-heading text-xl font-semibold mb-2">No Opportunities Found</h3>
          <p className="text-gray-400">Run a scan to discover new opportunities based on your interests</p>
        </GlassCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AnimatePresence>
            {opportunities.map((opp, idx) => (
              <motion.div
                key={opp.opportunity_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ delay: idx * 0.05 }}
              >
                <GlassCard className="p-6 h-full">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs border capitalize ${categoryColors[opp.category] || categoryColors.business}`}>
                        {opp.category}
                      </span>
                      <span className={`flex items-center gap-1 text-xs ${urgencyColors[opp.urgency]}`}>
                        <AlertCircle className="w-3 h-3" />
                        {opp.urgency} urgency
                      </span>
                    </div>
                    <button
                      onClick={() => dismissOpportunity(opp.opportunity_id)}
                      className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>

                  <h3 className="font-heading text-lg font-semibold mb-2">{opp.title}</h3>
                  <p className="text-sm text-gray-400 mb-4">{opp.description}</p>

                  {/* Potential Score */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-500">Potential Score</span>
                      <span className="text-primary font-medium">{opp.potential_score}/100</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${opp.potential_score}%` }}
                        className={`h-full ${
                          opp.potential_score >= 70 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                            : opp.potential_score >= 40 
                            ? 'bg-gradient-to-r from-yellow-500 to-orange-400'
                            : 'bg-gradient-to-r from-red-500 to-pink-400'
                        }`}
                      />
                    </div>
                  </div>

                  {/* Action Items */}
                  <div>
                    <h4 className="text-sm text-gray-500 mb-2">Action Items</h4>
                    <div className="space-y-2">
                      {opp.action_items?.slice(0, 3).map((action, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <Target className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
